/**
 * Document-level affordance for clipped text.
 *
 * Any element that clips its content — `truncate`, `line-clamp-*`, or anything carrying
 * `data-full-text` — shows its complete value in a teleported bubble on hover, keyboard
 * focus or touch press. Overflow is checked lazily at interaction time, so the cost is
 * paid only for the element under the pointer.
 *
 * Installed once from `main.ts`; no per-component wiring is required for legacy markup.
 */

const TARGET_SELECTOR = '[data-full-text], .truncate, [class*="line-clamp"]'
const TIP_CLASS = 'truncation-tip'
const SHOW_DELAY_MS = 350
const VIEWPORT_MARGIN = 8

let tip: HTMLDivElement | null = null
let active: HTMLElement | null = null
let showTimer: number | null = null

function ensureTip(): HTMLDivElement {
  if (tip?.isConnected) return tip
  const node = document.createElement('div')
  node.className = TIP_CLASS
  node.setAttribute('role', 'tooltip')
  node.hidden = true
  document.body.appendChild(node)
  tip = node
  return node
}

function isClipped(el: HTMLElement): boolean {
  return el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1
}

function textOf(el: HTMLElement): string {
  const explicit = el.dataset.fullText
  return (explicit ?? el.textContent ?? '').replace(/\s+/g, ' ').trim()
}

function resolveTarget(node: EventTarget | null): HTMLElement | null {
  if (!(node instanceof Element)) return null
  const el = node.closest<HTMLElement>(TARGET_SELECTOR)
  if (!el) return null
  if (el.dataset.noTruncationTip === 'true') return null
  // Never describe the tooltip with itself.
  if (el.closest(`.${TIP_CLASS}`)) return null
  return el
}

function place(el: HTMLElement): void {
  const bubble = ensureTip()
  const rect = el.getBoundingClientRect()
  bubble.hidden = false

  const width = bubble.offsetWidth
  const height = bubble.offsetHeight
  const left = Math.min(
    Math.max(VIEWPORT_MARGIN, rect.left + rect.width / 2 - width / 2),
    window.innerWidth - width - VIEWPORT_MARGIN,
  )
  let top = rect.top - height - VIEWPORT_MARGIN
  if (top < VIEWPORT_MARGIN) top = rect.bottom + VIEWPORT_MARGIN

  bubble.style.left = `${Math.round(left)}px`
  bubble.style.top = `${Math.round(top)}px`
}

function show(el: HTMLElement): void {
  if (!isClipped(el)) return
  const text = textOf(el)
  if (!text) return
  active = el
  const bubble = ensureTip()
  bubble.textContent = text
  place(el)
}

function hide(): void {
  if (showTimer !== null) {
    window.clearTimeout(showTimer)
    showTimer = null
  }
  active = null
  if (tip) tip.hidden = true
}

function scheduleShow(el: HTMLElement, delay: number): void {
  if (showTimer !== null) window.clearTimeout(showTimer)
  showTimer = window.setTimeout(() => show(el), delay)
}

function onPointerOver(event: PointerEvent): void {
  if (event.pointerType === 'touch') return
  const el = resolveTarget(event.target)
  if (!el || el === active) return
  scheduleShow(el, SHOW_DELAY_MS)
}

function onPointerOut(event: PointerEvent): void {
  const el = resolveTarget(event.target)
  if (!el) return
  const next = event.relatedTarget
  if (next instanceof Node && el.contains(next)) return
  hide()
}

function onFocusIn(event: FocusEvent): void {
  const el = resolveTarget(event.target)
  if (el) scheduleShow(el, 0)
}

function onFocusOut(): void {
  hide()
}

function onKeyDown(event: KeyboardEvent): void {
  if (event.key === 'Escape') hide()
}

/**
 * Registers the delegated listeners once for the lifetime of the app.
 * Safe to call more than once during HMR: a marker prevents duplicate listeners.
 */
export function installTruncationAffordance(): void {
  const marker = '__tanseqTruncationAffordance'
  const target = window as unknown as Record<string, boolean>
  if (target[marker]) return
  target[marker] = true

  document.addEventListener('pointerover', onPointerOver, { passive: true })
  document.addEventListener('pointerout', onPointerOut, { passive: true })
  document.addEventListener('focusin', onFocusIn)
  document.addEventListener('focusout', onFocusOut)
  document.addEventListener('keydown', onKeyDown)
  window.addEventListener('scroll', hide, true)
  window.addEventListener('resize', hide)
}
